import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Upload, Loader2, CheckCircle2, X, FileText, Image as ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MAX_MB = 10;
const ACCEPT = ".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf";

const COPY = {
  en: {
    title: "Field upload",
    subtitle: "Add photos and documents for this project.",
    linked_to: "For",
    from: "From",
    manager: "Project manager",
    expires: "Expires",
    upload_prompt: "Choose files or drag them here",
    format_hint: `Photos or PDF, up to ${MAX_MB} MB each`,
    optional_note: "Optional note (e.g. what changed today)",
    phase_label: "Evidence phase",
    phases: {
      "": "— none —",
      before: "Before",
      progress: "Progress / WIP",
      completed: "Completed",
      damage: "Damage",
      general: "General",
    },
    uploading: "Uploading…",
    uploaded: "Uploaded",
    submit: "Submit and finish",
    submitted: "Submitted. You can close this page.",
    err_load: "This link is no longer available.",
    err_upload: "Upload failed",
  },
  es: {
    title: "Subida desde la obra",
    subtitle: "Agrega fotos y documentos de este proyecto.",
    linked_to: "Para",
    from: "De parte de",
    manager: "Jefe de proyecto",
    expires: "Vence",
    upload_prompt: "Elige archivos o arrástralos aquí",
    format_hint: `Fotos o PDF, hasta ${MAX_MB} MB cada uno`,
    optional_note: "Nota opcional (por ejemplo, qué cambió hoy)",
    phase_label: "Fase de la evidencia",
    phases: {
      "": "— ninguna —",
      before: "Antes",
      progress: "En progreso",
      completed: "Terminado",
      damage: "Daño",
      general: "General",
    },
    uploading: "Subiendo…",
    uploaded: "Subido",
    submit: "Enviar y terminar",
    submitted: "Enviado. Ya puedes cerrar la página.",
    err_load: "Este enlace ya no está disponible.",
    err_upload: "Fallo al subir",
  },
};

export default function FieldEvidence() {
  const { token } = useParams();
  const [boot, setBoot] = useState(null);
  const [bootErr, setBootErr] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/field-evidence/${token}`);
        if (alive) setBoot(data);
      } catch (e) {
        if (alive) setBootErr(e?.response?.status || "load_failed");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const lang = (boot?.language || "en").startsWith("es") ? "es" : "en";
  const c = COPY[lang];

  const handleFiles = useCallback(async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name}: > ${MAX_MB} MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      if (note) fd.append("note", note);
      if (phase) fd.append("evidence_phase", phase);
      try {
        const { data } = await axios.post(
          `${API}/api/public/field-evidence/${token}/upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setFiles(prev => [{ id: data.file_id, name: f.name, size: f.size, type: f.type }, ...prev]);
      } catch (e) {
        toast.error(`${c.err_upload}: ${f.name}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }, [token, note, phase, c.err_upload]);

  const submit = useCallback(async () => {
    if (!files.length) return;
    try {
      await axios.post(`${API}/api/public/field-evidence/${token}/submit`);
      setSubmitted(true);
      toast.success(c.submitted);
    } catch (e) {
      toast.error(c.err_upload);
    }
  }, [token, files.length, c.err_upload, c.submitted]);

  if (bootErr) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-white rounded-xl shadow-sm border p-6 text-center">
          <div className="text-slate-700 text-sm" data-testid="fe-err-load">
            {COPY.en.err_load}
          </div>
        </div>
      </div>
    );
  }

  if (!boot) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" />
      </div>
    );
  }

  const expShort = boot.expires_at ? new Date(boot.expires_at).toLocaleDateString(
    lang === "es" ? "es-ES" : "en-US",
    { month: "short", day: "numeric", year: "numeric" },
  ) : "";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="field-evidence-page">
      <Toaster position="top-center" richColors />
      <div className="max-w-md mx-auto px-4 py-6 space-y-5">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="fe-title">
            {c.title}
          </h1>
          <p className="text-sm text-slate-500" data-testid="fe-subtitle">{c.subtitle}</p>
        </header>

        <div className="bg-white rounded-xl border p-4 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{c.linked_to}</span>
            <span className="font-medium" data-testid="fe-project-label">{boot.project_label}</span>
          </div>
          {boot.project_address ? (
            <div className="flex items-center justify-between">
              <span className="text-slate-500">·</span>
              <span className="text-slate-600 text-xs" data-testid="fe-project-address">
                {boot.project_address}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{c.from}</span>
            <span className="font-medium" data-testid="fe-company-name">{boot.company_name}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{c.manager}</span>
            <span className="font-medium" data-testid="fe-assignee-name">{boot.assignee_display_name}</span>
          </div>
          {expShort ? (
            <div className="flex items-center justify-between pt-1 text-xs text-slate-500 border-t">
              <span>{c.expires}</span>
              <span data-testid="fe-expires">{expShort}</span>
            </div>
          ) : null}
        </div>

        {!submitted ? (
          <>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              className="bg-white rounded-xl border-2 border-dashed border-slate-300 p-6 text-center"
            >
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-3">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                <span>{uploading ? c.uploading : c.upload_prompt}</span>
              </div>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPT}
                capture="environment"
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="fe-file-input"
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md bg-slate-900 text-white disabled:opacity-60"
                data-testid="fe-pick-btn"
              >
                <Upload size={14} /> {c.upload_prompt}
              </button>
              <div className="mt-2 text-xs text-slate-500">{c.format_hint}</div>
            </div>

            <div className="bg-white rounded-xl border p-4 space-y-3">
              <label className="text-xs uppercase tracking-wide text-slate-500">
                {c.phase_label}
              </label>
              <select
                value={phase}
                onChange={(e) => setPhase(e.target.value)}
                className="w-full text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                data-testid="fe-phase-select"
              >
                {Object.entries(c.phases).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={c.optional_note}
                className="w-full text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                data-testid="fe-note-input"
              />
            </div>

            {files.length ? (
              <div className="bg-white rounded-xl border divide-y">
                {files.map(f => (
                  <div key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`fe-file-row-${f.id}`}>
                    {String(f.type).startsWith("image/") ? <ImageIcon size={14} /> : <FileText size={14} />}
                    <span className="flex-1 truncate">{f.name}</span>
                    <CheckCircle2 size={14} className="text-emerald-500" />
                  </div>
                ))}
              </div>
            ) : null}

            <button
              type="button"
              disabled={!files.length || uploading}
              onClick={submit}
              className="w-full py-3 rounded-md bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="fe-submit-btn"
            >
              {c.submit}
            </button>
          </>
        ) : (
          <div className="bg-white rounded-xl border p-6 text-center space-y-2" data-testid="fe-submitted">
            <CheckCircle2 className="text-emerald-500 mx-auto" />
            <div className="text-sm">{c.submitted}</div>
          </div>
        )}
      </div>
    </div>
  );
}
